import { Link } from "react-router-dom";

import { ChevronDown, LogOut, UserCircle } from "lucide-react";

import { useSignOut } from "@/features/auth/application/use-sign-out";
import { Button } from "@/shared/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/components/ui/dropdown-menu";
import { cn } from "@/shared/lib/utils";
import { useAppSelector } from "@/store";
import { selectAdmin } from "@/store/selectors";

function initialsFromName(name: string | undefined): string {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

/**
 * Dropdown del user en el navbar.
 *
 * Trigger: avatar con iniciales (Fraunces display) + primer nombre +
 * chevron. En mobile solo el avatar — el nombre se ve en el panel.
 *
 * Menú: nombre + email mono · "Mi cuenta" · divisor · "Cerrar sesión".
 * No incluimos "Preferencias" porque no hay página de preferencias;
 * cuando exista se agrega. Honestos con la data real.
 */
export function UserMenu() {
  const admin = useAppSelector(selectAdmin);
  const { signOut, isLoading } = useSignOut();

  if (!admin) return null;

  const initials = initialsFromName(admin.full_name);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-10 gap-2 px-1.5 hover:bg-accent/40"
          aria-label="Abrir menú de usuario"
        >
          <div
            aria-hidden
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/15"
          >
            <span className="font-display text-[12px] font-medium tracking-display-tight">
              {initials}
            </span>
          </div>
          <span className="hidden text-[13px] font-medium text-foreground md:inline">
            {admin.full_name.split(" ")[0]}
          </span>
          <ChevronDown aria-hidden className="hidden h-3.5 w-3.5 text-muted-foreground md:inline" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="font-normal">
          <div className="flex items-center gap-3 py-1">
            <div
              aria-hidden
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary ring-1 ring-primary/15"
            >
              <span className="font-display text-[14px] font-medium tracking-display-tight">
                {initials}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-[13.5px] font-medium text-foreground">
                {admin.full_name}
              </div>
              <div className="truncate font-mono text-[11px] text-muted-foreground">
                {admin.email}
              </div>
            </div>
          </div>
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-2 py-0.5">
            <span
              aria-hidden
              className="font-mono text-[9px] font-medium uppercase tracking-[0.22em] text-primary"
            >
              Rol
            </span>
            <span className="text-[11px] font-medium text-primary">{admin.role}</span>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/perfil" className="cursor-pointer">
            <UserCircle className="text-muted-foreground" />
            Mi cuenta
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={signOut}
          disabled={isLoading}
          className={cn(
            "cursor-pointer text-destructive focus:bg-destructive/10 focus:text-destructive",
          )}
        >
          <LogOut className="text-destructive" />
          {isLoading ? "Cerrando sesión…" : "Cerrar sesión"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
